import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, TrendingUp, TrendingDown, MessageSquare, X, ChevronRight, ThumbsUp, ThumbsDown, Minus, MapPin, Calendar, Filter, Store } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { KPICard } from '../components/ui/KPICard';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
// Region filter options
const regions = [{
  id: 'konzern',
  name: 'Gesamtkonzern',
  icon: Building2
}, {
  id: 'nord',
  name: 'Fielmann Nord'
}, {
  id: 'minden-hannover',
  name: 'Fielmann Ost'
}, {
  id: 'rhein-ruhr',
  name: 'Fielmann West'
}, {
  id: 'hessenring',
  name: 'Fielmann Mitte'
}, {
  id: 'nordbayern',
  name: 'Fielmann Sachsen-Thüringen'
}, {
  id: 'suedwest',
  name: 'Fielmann Südwest'
}, {
  id: 'suedbayern',
  name: 'Fielmann Bayern'
}];
// Mock data per region
const regionData: Record<string, {
  positive: number;
  neutral: number;
  negative: number;
  total: number;
  avgRating: number;
  trend: number;
}> = {
  konzern: {
    positive: 5820,
    neutral: 1980,
    negative: 620,
    total: 8420,
    avgRating: 4.3,
    trend: 0.18
  },
  nord: {
    positive: 920,
    neutral: 280,
    negative: 80,
    total: 1280,
    avgRating: 4.4,
    trend: 0.22
  },
  'minden-hannover': {
    positive: 720,
    neutral: 250,
    negative: 80,
    total: 1050,
    avgRating: 4.2,
    trend: 0.12
  },
  'rhein-ruhr': {
    positive: 950,
    neutral: 340,
    negative: 130,
    total: 1420,
    avgRating: 4.1,
    trend: 0.08
  },
  hessenring: {
    positive: 620,
    neutral: 180,
    negative: 50,
    total: 850,
    avgRating: 4.35,
    trend: 0.2
  },
  nordbayern: {
    positive: 650,
    neutral: 210,
    negative: 60,
    total: 920,
    avgRating: 4.25,
    trend: 0.15
  },
  suedwest: {
    positive: 820,
    neutral: 260,
    negative: 70,
    total: 1150,
    avgRating: 4.3,
    trend: 0.18
  },
  suedbayern: {
    positive: 940,
    neutral: 240,
    negative: 70,
    total: 1250,
    avgRating: 4.5,
    trend: 0.25
  }
};
// Topic clusters data - Optician relevant
const topicClusters: Record<string, Array<{
  name: string;
  size: number;
  sentiment: 'positive' | 'neutral' | 'negative';
  comments: Array<{
    text: string;
    market: string;
    date: string;
    rating: number;
  }>;
}>> = {
  konzern: [{
    name: 'Brillenqualität',
    size: 1850,
    sentiment: 'positive',
    comments: [{
      text: 'Die Brillenqualität ist hervorragend, meine neue Gleitsichtbrille sitzt perfekt!',
      market: 'Fielmann Hamburg',
      date: '2024-01-28',
      rating: 5
    }, {
      text: 'Beste Gläser die ich je hatte, kristallklar und entspiegelt.',
      market: 'Fielmann München-Schwabing',
      date: '2024-01-27',
      rating: 5
    }, {
      text: 'Meine Brille wurde perfekt angepasst, sehr zufrieden.',
      market: 'Fielmann Frankfurt-Nordend',
      date: '2024-01-26',
      rating: 5
    }, {
      text: 'Die Qualität der Gläser ist top, auch nach einem Jahr noch wie neu.',
      market: 'Fielmann Kiel',
      date: '2024-01-25',
      rating: 5
    }, {
      text: 'Hochwertige Fassungen und exzellente Verarbeitung.',
      market: 'Fielmann Freiburg',
      date: '2024-01-24',
      rating: 4
    }]
  }, {
    name: 'Beratungskompetenz',
    size: 1420,
    sentiment: 'positive',
    comments: [{
      text: 'Die Beratung war erstklassig, der Optiker hat sich viel Zeit genommen.',
      market: 'Fielmann Dresden-Altstadt',
      date: '2024-01-28',
      rating: 5
    }, {
      text: 'Sehr kompetente Beratung bei der Auswahl meiner Gleitsichtgläser!',
      market: 'Fielmann Stuttgart-West',
      date: '2024-01-27',
      rating: 5
    }, {
      text: 'Freundliches und fachkundiges Personal, fühlte mich gut aufgehoben.',
      market: 'Fielmann Hannover-List',
      date: '2024-01-26',
      rating: 4
    }]
  }, {
    name: 'Wartezeiten',
    size: 1180,
    sentiment: 'negative',
    comments: [{
      text: 'Über 45 Minuten Wartezeit für einen Sehtest, das ist zu lang.',
      market: 'Fielmann Berlin-Kreuzberg',
      date: '2024-01-28',
      rating: 2
    }, {
      text: 'Samstags ist es unmöglich hier einen Termin zu bekommen.',
      market: 'Fielmann Köln-Ehrenfeld',
      date: '2024-01-27',
      rating: 1
    }, {
      text: 'Bitte mehr Personal für die Sehtests einstellen!',
      market: 'Fielmann Dortmund',
      date: '2024-01-26',
      rating: 2
    }, {
      text: 'Wartezeit von einer Stunde für die Brillenanpassung ist nicht akzeptabel.',
      market: 'Fielmann Duisburg',
      date: '2024-01-25',
      rating: 2
    }]
  }, {
    name: 'Brillenauswahl',
    size: 1050,
    sentiment: 'positive',
    comments: [{
      text: 'Riesige Auswahl an Fassungen, für jeden Geschmack etwas dabei.',
      market: 'Fielmann München',
      date: '2024-01-28',
      rating: 5
    }, {
      text: 'Tolle Designerbrillen und auch günstige Alternativen!',
      market: 'Fielmann Frankfurt-Sachsenhausen',
      date: '2024-01-27',
      rating: 5
    }]
  }, {
    name: 'Preis-Leistung',
    size: 920,
    sentiment: 'neutral',
    comments: [{
      text: 'Preise sind fair für die gebotene Qualität.',
      market: 'Fielmann Leipzig-Zentrum',
      date: '2024-01-28',
      rating: 3
    }, {
      text: 'Gutes Preis-Leistungs-Verhältnis, besonders bei den Eigenmarken.',
      market: 'Fielmann Nürnberg',
      date: '2024-01-27',
      rating: 4
    }, {
      text: 'Etwas teurer als Online-Anbieter, aber dafür mit persönlicher Beratung.',
      market: 'Fielmann Bremen',
      date: '2024-01-26',
      rating: 3
    }]
  }, {
    name: 'Ladengestaltung',
    size: 780,
    sentiment: 'positive',
    comments: [{
      text: 'Modernes und einladendes Ambiente, sehr angenehm.',
      market: 'Fielmann Karlsruhe',
      date: '2024-01-28',
      rating: 5
    }, {
      text: 'Übersichtliche Präsentation der Brillen, macht die Auswahl leicht.',
      market: 'Fielmann Düsseldorf-Bilk',
      date: '2024-01-27',
      rating: 5
    }]
  }, {
    name: 'Erreichbarkeit',
    size: 650,
    sentiment: 'negative',
    comments: [{
      text: 'Parkplatzsituation in der Innenstadt ist schwierig.',
      market: 'Fielmann Berlin-Mitte',
      date: '2024-01-28',
      rating: 2
    }, {
      text: 'Keine Parkplätze in der Nähe, musste weit laufen.',
      market: 'Fielmann Hamburg-Altona',
      date: '2024-01-27',
      rating: 3
    }]
  }, {
    name: 'Terminvergabe',
    size: 520,
    sentiment: 'neutral',
    comments: [{
      text: 'Online-Terminbuchung funktioniert gut.',
      market: 'Fielmann Essen',
      date: '2024-01-28',
      rating: 3
    }, {
      text: 'Termine sind oft erst in 2-3 Wochen verfügbar.',
      market: 'Fielmann Bonn',
      date: '2024-01-27',
      rating: 3
    }]
  }],
  nord: [{
    name: 'Brillenqualität',
    size: 280,
    sentiment: 'positive',
    comments: []
  }, {
    name: 'Beratungskompetenz',
    size: 220,
    sentiment: 'positive',
    comments: []
  }, {
    name: 'Kontaktlinsen-Service',
    size: 180,
    sentiment: 'positive',
    comments: []
  }, {
    name: 'Wartezeiten',
    size: 120,
    sentiment: 'negative',
    comments: []
  }, {
    name: 'Brillenauswahl',
    size: 160,
    sentiment: 'positive',
    comments: []
  }]
};
// 12-month trend data
const monthlyTrendData: Record<string, Array<{
  month: string;
  rating: number;
  reviews: number;
  positivePercent: number;
  neutralPercent: number;
  negativePercent: number;
}>> = {
  konzern: [{
    month: 'Feb 23',
    rating: 4.15,
    reviews: 6800,
    positivePercent: 65,
    neutralPercent: 26,
    negativePercent: 9
  }, {
    month: 'Mär 23',
    rating: 4.18,
    reviews: 7100,
    positivePercent: 66,
    neutralPercent: 25,
    negativePercent: 9
  }, {
    month: 'Apr 23',
    rating: 4.16,
    reviews: 6950,
    positivePercent: 66,
    neutralPercent: 25,
    negativePercent: 9
  }, {
    month: 'Mai 23',
    rating: 4.2,
    reviews: 7300,
    positivePercent: 67,
    neutralPercent: 25,
    negativePercent: 8
  }, {
    month: 'Jun 23',
    rating: 4.22,
    reviews: 7600,
    positivePercent: 68,
    neutralPercent: 24,
    negativePercent: 8
  }, {
    month: 'Jul 23',
    rating: 4.2,
    reviews: 7450,
    positivePercent: 67,
    neutralPercent: 25,
    negativePercent: 8
  }, {
    month: 'Aug 23',
    rating: 4.23,
    reviews: 7700,
    positivePercent: 68,
    neutralPercent: 24,
    negativePercent: 8
  }, {
    month: 'Sep 23',
    rating: 4.25,
    reviews: 7900,
    positivePercent: 69,
    neutralPercent: 24,
    negativePercent: 7
  }, {
    month: 'Okt 23',
    rating: 4.26,
    reviews: 8100,
    positivePercent: 69,
    neutralPercent: 24,
    negativePercent: 7
  }, {
    month: 'Nov 23',
    rating: 4.28,
    reviews: 8250,
    positivePercent: 70,
    neutralPercent: 23,
    negativePercent: 7
  }, {
    month: 'Dez 23',
    rating: 4.29,
    reviews: 8350,
    positivePercent: 70,
    neutralPercent: 23,
    negativePercent: 7
  }, {
    month: 'Jan 24',
    rating: 4.3,
    reviews: 8420,
    positivePercent: 71,
    neutralPercent: 22,
    negativePercent: 7
  }],
  nord: [{
    month: 'Feb 23',
    rating: 4.25,
    reviews: 1050,
    positivePercent: 68,
    neutralPercent: 24,
    negativePercent: 8
  }, {
    month: 'Mär 23',
    rating: 4.27,
    reviews: 1080,
    positivePercent: 69,
    neutralPercent: 23,
    negativePercent: 8
  }, {
    month: 'Apr 23',
    rating: 4.3,
    reviews: 1100,
    positivePercent: 70,
    neutralPercent: 23,
    negativePercent: 7
  }, {
    month: 'Mai 23',
    rating: 4.32,
    reviews: 1130,
    positivePercent: 71,
    neutralPercent: 22,
    negativePercent: 7
  }, {
    month: 'Jun 23',
    rating: 4.34,
    reviews: 1160,
    positivePercent: 72,
    neutralPercent: 22,
    negativePercent: 6
  }, {
    month: 'Jul 23',
    rating: 4.35,
    reviews: 1180,
    positivePercent: 72,
    neutralPercent: 22,
    negativePercent: 6
  }, {
    month: 'Aug 23',
    rating: 4.36,
    reviews: 1200,
    positivePercent: 73,
    neutralPercent: 21,
    negativePercent: 6
  }, {
    month: 'Sep 23',
    rating: 4.37,
    reviews: 1220,
    positivePercent: 73,
    neutralPercent: 21,
    negativePercent: 6
  }, {
    month: 'Okt 23',
    rating: 4.38,
    reviews: 1240,
    positivePercent: 74,
    neutralPercent: 20,
    negativePercent: 6
  }, {
    month: 'Nov 23',
    rating: 4.39,
    reviews: 1260,
    positivePercent: 74,
    neutralPercent: 20,
    negativePercent: 6
  }, {
    month: 'Dez 23',
    rating: 4.4,
    reviews: 1270,
    positivePercent: 75,
    neutralPercent: 19,
    negativePercent: 6
  }, {
    month: 'Jan 24',
    rating: 4.42,
    reviews: 1280,
    positivePercent: 75,
    neutralPercent: 19,
    negativePercent: 6
  }]
};
const timePeriods = [{
  id: '30',
  label: '30 Tage'
}, {
  id: '60',
  label: '60 Tage'
}, {
  id: '120',
  label: '120 Tage'
}, {
  id: '365',
  label: '365 Tage'
}, {
  id: 'gesamt',
  label: 'Gesamt'
}];
function applyTimeFactor(value: number, period: string, isRating: boolean = false): number {
  if (period === 'gesamt') return value;
  if (isRating) {
    const noise = period === '30' ? 0.1 : period === '60' ? -0.1 : period === '120' ? 0.05 : -0.05;
    return Math.max(1, Math.min(5, value + noise));
  }
  const factors: Record<string, number> = {
    '30': 0.08,
    '60': 0.17,
    '120': 0.33,
    '365': 1.0
  };
  return Math.max(1, Math.round(value * (factors[period] || 1)));
}
// Simple custom treemap component
const SimpleTreemap = ({
  data,
  onClick
}: {
  data: Array<{
    name: string;
    size: number;
    sentiment: string;
  }>;
  onClick: (item: any) => void;
}) => {
  const total = data.reduce((sum, item) => sum + item.size, 0);
  const sortedData = [...data].sort((a, b) => b.size - a.size);
  const getColors = (sentiment: string) => {
    switch (sentiment) {
      case 'positive':
        return {
          bg: '#d4edda',
          border: '#639754',
          text: '#1a3d1a'
        };
      case 'negative':
        return {
          bg: '#f8d7da',
          border: '#E03C32',
          text: '#3d0a0a'
        };
      default:
        return {
          bg: '#f9f5eb',
          border: '#c5a55a',
          text: '#3d3200'
        };
    }
  };
  return <div className="grid grid-cols-4 gap-2 h-full" data-id="element-507">
      {sortedData.map((item, index) => {
      const colors = getColors(item.sentiment);
      let colSpan = 1;
      let rowSpan = 1;
      if (index === 0) {
        colSpan = 2;
        rowSpan = 2;
      } else if (index === 1 || index === 2) {
        colSpan = 2;
        rowSpan = 1;
      }
      return <div key={item.name} onClick={() => onClick(item)} className="rounded-lg p-3 flex flex-col justify-center items-center text-center cursor-pointer transition-opacity hover:opacity-80" style={{
        backgroundColor: colors.bg,
        border: `2px solid ${colors.border}`,
        gridColumn: `span ${colSpan}`,
        gridRow: `span ${rowSpan}`
      }} data-id="element-508">
            <span className="font-semibold text-sm leading-tight" style={{
          color: colors.text
        }} data-id="element-509">
              {item.name}
            </span>
            <span className="text-xs mt-1 opacity-70" style={{
          color: colors.text
        }} data-id="element-510">
              {item.size.toLocaleString('de-DE')}
            </span>
          </div>;
    })}
    </div>;
};
export function KonzernStimmungPage() {
  const [selectedRegion, setSelectedRegion] = useState('konzern');
  const [selectedPeriod, setSelectedPeriod] = useState('gesamt');
  const [selectedTopic, setSelectedTopic] = useState<(typeof topicClusters)['konzern'][0] | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const rawData = regionData[selectedRegion] || regionData.konzern;
  const rawTopics = topicClusters[selectedRegion] || topicClusters.konzern;
  const rawTrend = monthlyTrendData[selectedRegion] || monthlyTrendData.konzern;
  const currentData = {
    ...rawData,
    positive: applyTimeFactor(rawData.positive, selectedPeriod),
    neutral: applyTimeFactor(rawData.neutral, selectedPeriod),
    negative: applyTimeFactor(rawData.negative, selectedPeriod),
    total: applyTimeFactor(rawData.total, selectedPeriod),
    avgRating: applyTimeFactor(rawData.avgRating, selectedPeriod, true)
  };
  const currentTopics = rawTopics.map(t => ({
    ...t,
    size: applyTimeFactor(t.size, selectedPeriod)
  }));
  let currentTrend = [...rawTrend];
  if (selectedPeriod === '30') currentTrend = rawTrend.slice(-1);else if (selectedPeriod === '60') currentTrend = rawTrend.slice(-2);else if (selectedPeriod === '120') currentTrend = rawTrend.slice(-4);
  const totalSentiments = currentData.positive + currentData.neutral + currentData.negative;
  const positivePercent = Math.round(currentData.positive / totalSentiments * 100);
  const neutralPercent = Math.round(currentData.neutral / totalSentiments * 100);
  const negativePercent = Math.round(currentData.negative / totalSentiments * 100);
  const handleTopicClick = (topic: any) => {
    const fullTopic = currentTopics.find(t => t.name === topic.name);
    if (fullTopic && fullTopic.comments.length > 0) {
      setSelectedTopic(fullTopic);
      setIsDialogOpen(true);
    }
  };
  const treemapData = currentTopics.map(topic => ({
    name: topic.name,
    size: topic.size,
    sentiment: topic.sentiment
  }));
  return <div className="p-6 space-y-6 overflow-y-auto h-[calc(100vh-64px)]" data-id="element-511">
      {/* Region Filter */}
      <Card className="!p-4" data-id="element-512">
        <div className="flex flex-col gap-4" data-id="element-513">
          <div className="flex items-center gap-4 flex-wrap" data-id="element-514">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700" data-id="element-515">
              <Filter size={16} data-id="element-516" />
              <span data-id="element-517">Region:</span>
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-518">
              {regions.map(region => {
              const isSelected = selectedRegion === region.id;
              const Icon = region.icon;
              return <button key={region.id} onClick={() => setSelectedRegion(region.id)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${isSelected ? 'bg-edeka-blue text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-519">
                    {Icon && <Icon size={14} data-id="element-520" />}
                    {region.name}
                  </button>;
            })}
            </div>
          </div>

          <div className="flex items-center gap-4 flex-wrap" data-id="element-521">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700" data-id="element-522">
              <Calendar size={16} data-id="element-523" />
              <span data-id="element-524">Zeitraum:</span>
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-525">
              {timePeriods.map(period => {
              const isSelected = selectedPeriod === period.id;
              return <button key={period.id} onClick={() => setSelectedPeriod(period.id)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${isSelected ? 'bg-edeka-blue text-white shadow-md' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-526">
                    {period.label}
                  </button>;
            })}
            </div>
          </div>
        </div>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-527">
        <KPICard title="Durchschnittliche Bewertung" value={currentData.avgRating.toFixed(1)} icon={<TrendingUp size={18} data-id="element-529" />} data-id="element-528" />
        <KPICard title="Gesamtbewertungen" value={currentData.total.toLocaleString('de-DE')} icon={<MessageSquare size={18} data-id="element-531" />} data-id="element-530" />
        <KPICard title="Positive Resonanz" value={`${positivePercent}%`} icon={<ThumbsUp size={18} data-id="element-533" />} data-id="element-532" />
        <KPICard title="Negative Resonanz" value={`${negativePercent}%`} icon={<ThumbsDown size={18} data-id="element-535" />} data-id="element-534" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-536">
        {/* Absolute Resonanz */}
        <Card data-id="element-537">
          <div className="p-6" data-id="element-538">
            <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-539">
              Absolute Resonanz
            </h3>
            <p className="text-sm text-gray-500 mb-6" data-id="element-540">
              Verteilung der Sentiment-Analyse
            </p>

            <div className="mb-6" data-id="element-541">
              <div className="h-12 rounded-lg overflow-hidden flex" data-id="element-542">
                <div style={{
                width: `${positivePercent}%`,
                backgroundColor: '#639754'
              }} className="flex items-center justify-center" data-id="element-543">
                  {positivePercent > 10 && <span className="text-white text-sm font-semibold" data-id="element-544">
                      {positivePercent}%
                    </span>}
                </div>
                <div style={{
                width: `${neutralPercent}%`,
                backgroundColor: '#c5a55a'
              }} className="flex items-center justify-center" data-id="element-545">
                  {neutralPercent > 10 && <span className="text-gray-800 text-sm font-semibold" data-id="element-546">
                      {neutralPercent}%
                    </span>}
                </div>
                <div style={{
                width: `${negativePercent}%`,
                backgroundColor: '#E03C32'
              }} className="flex items-center justify-center" data-id="element-547">
                  {negativePercent > 10 && <span className="text-white text-sm font-semibold" data-id="element-548">
                      {negativePercent}%
                    </span>}
                </div>
              </div>
            </div>

            <div className="space-y-3" data-id="element-549">
              <div className="flex items-center justify-between p-3 rounded-lg" style={{
              backgroundColor: '#e8f0e5'
            }} data-id="element-550">
                <div className="flex items-center gap-3" data-id="element-551">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{
                  backgroundColor: '#639754'
                }} data-id="element-552">
                    <ThumbsUp size={18} className="text-white" data-id="element-553" />
                  </div>
                  <div data-id="element-554">
                    <div className="font-semibold text-gray-900" data-id="element-555">Positiv</div>
                    <div className="text-xs text-gray-500" data-id="element-556">
                      Zufriedene Kunden
                    </div>
                  </div>
                </div>
                <div className="text-right" data-id="element-557">
                  <div className="text-2xl font-bold" style={{
                  color: '#639754'
                }} data-id="element-558">
                    {currentData.positive.toLocaleString('de-DE')}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-559">
                    {positivePercent}% aller Bewertungen
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg" style={{
              backgroundColor: '#f9f5eb'
            }} data-id="element-560">
                <div className="flex items-center gap-3" data-id="element-561">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{
                  backgroundColor: '#c5a55a'
                }} data-id="element-562">
                    <Minus size={18} className="text-gray-800" data-id="element-563" />
                  </div>
                  <div data-id="element-564">
                    <div className="font-semibold text-gray-900" data-id="element-565">Neutral</div>
                    <div className="text-xs text-gray-500" data-id="element-566">
                      Gemischte Meinungen
                    </div>
                  </div>
                </div>
                <div className="text-right" data-id="element-567">
                  <div className="text-2xl font-bold" style={{
                  color: '#9a7d3a'
                }} data-id="element-568">
                    {currentData.neutral.toLocaleString('de-DE')}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-569">
                    {neutralPercent}% aller Bewertungen
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg" style={{
              backgroundColor: '#fce8e7'
            }} data-id="element-570">
                <div className="flex items-center gap-3" data-id="element-571">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{
                  backgroundColor: '#E03C32'
                }} data-id="element-572">
                    <ThumbsDown size={18} className="text-white" data-id="element-573" />
                  </div>
                  <div data-id="element-574">
                    <div className="font-semibold text-gray-900" data-id="element-575">Negativ</div>
                    <div className="text-xs text-gray-500" data-id="element-576">
                      Unzufriedene Kunden
                    </div>
                  </div>
                </div>
                <div className="text-right" data-id="element-577">
                  <div className="text-2xl font-bold" style={{
                  color: '#E03C32'
                }} data-id="element-578">
                    {currentData.negative.toLocaleString('de-DE')}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-579">
                    {negativePercent}% aller Bewertungen
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Themen Cluster Treemap */}
        <Card data-id="element-580">
          <div className="p-6" data-id="element-581">
            <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-582">
              Themen-Cluster
            </h3>
            <p className="text-sm text-gray-500 mb-4" data-id="element-583">
              Häufig erwähnte Themen in Bewertungen (klicken für Details)
            </p>

            <div className="h-80" data-id="element-584">
              <SimpleTreemap data={treemapData} onClick={handleTopicClick} data-id="element-585" />
            </div>

            <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t border-gray-100" data-id="element-586">
              <div className="flex items-center gap-2" data-id="element-587">
                <div className="w-3 h-3 rounded" style={{
                backgroundColor: '#639754'
              }} data-id="element-588"></div>
                <span className="text-xs text-gray-600" data-id="element-589">Positiv</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-590">
                <div className="w-3 h-3 rounded" style={{
                backgroundColor: '#c5a55a'
              }} data-id="element-591"></div>
                <span className="text-xs text-gray-600" data-id="element-592">Neutral</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-593">
                <div className="w-3 h-3 rounded" style={{
                backgroundColor: '#E03C32'
              }} data-id="element-594"></div>
                <span className="text-xs text-gray-600" data-id="element-595">Negativ</span>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* 12-Month Trend Chart */}
      <Card data-id="element-596">
        <div className="p-6" data-id="element-597">
          <div className="flex items-center justify-between mb-6" data-id="element-598">
            <div data-id="element-599">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-600">
                Reputationsentwicklung
              </h3>
              <p className="text-sm text-gray-500" data-id="element-601">
                Sentiment-Verteilung der letzten 12 Monate (in %)
              </p>
            </div>
            <div className="flex items-center gap-5 text-sm" data-id="element-602">
              <div className="flex items-center gap-2" data-id="element-603">
                <div className="w-3 h-3 rounded-full" style={{
                backgroundColor: '#639754'
              }} data-id="element-604"></div>
                <span className="text-gray-600" data-id="element-605">Positiv</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-606">
                <div className="w-3 h-3 rounded-full" style={{
                backgroundColor: '#c5a55a'
              }} data-id="element-607"></div>
                <span className="text-gray-600" data-id="element-608">Neutral</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-609">
                <div className="w-3 h-3 rounded-full" style={{
                backgroundColor: '#E03C32'
              }} data-id="element-610"></div>
                <span className="text-gray-600" data-id="element-611">Negativ</span>
              </div>
            </div>
          </div>

          <div className="h-72" data-id="element-612">
            <ResponsiveContainer width="100%" height="100%" data-id="element-613">
              <LineChart data={currentTrend} data-id="element-614">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} data-id="element-615" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} data-id="element-616" />
                <YAxis domain={[0, 100]} axisLine={false} tickLine={false} tick={{
                fontSize: 11,
                fill: '#9ca3af'
              }} tickFormatter={value => `${value}%`} data-id="element-617" />
                <Tooltip contentStyle={{
                borderRadius: '8px',
                border: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                fontSize: '12px'
              }} formatter={(value: number, name: string) => {
                const labels: Record<string, string> = {
                  positivePercent: 'Positiv',
                  neutralPercent: 'Neutral',
                  negativePercent: 'Negativ'
                };
                return [`${value}%`, labels[name] || name];
              }} data-id="element-618" />
                <Line type="monotone" dataKey="positivePercent" stroke="#639754" strokeWidth={3} dot={{
                fill: '#639754',
                strokeWidth: 2,
                r: 4
              }} activeDot={{
                r: 6,
                fill: '#639754',
                stroke: '#fff',
                strokeWidth: 2
              }} isAnimationActive={false} data-id="element-619" />
                <Line type="monotone" dataKey="neutralPercent" stroke="#c5a55a" strokeWidth={3} dot={{
                fill: '#c5a55a',
                strokeWidth: 2,
                r: 4
              }} activeDot={{
                r: 6,
                fill: '#c5a55a',
                stroke: '#fff',
                strokeWidth: 2
              }} isAnimationActive={false} data-id="element-620" />
                <Line type="monotone" dataKey="negativePercent" stroke="#E03C32" strokeWidth={3} dot={{
                fill: '#E03C32',
                strokeWidth: 2,
                r: 4
              }} activeDot={{
                r: 6,
                fill: '#E03C32',
                stroke: '#fff',
                strokeWidth: 2
              }} isAnimationActive={false} data-id="element-621" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {currentTrend.length > 1 && <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between flex-wrap gap-4" data-id="element-622">
              <div className="flex items-center gap-6" data-id="element-623">
                <div className="flex items-center gap-2" data-id="element-624">
                  <TrendingUp size={16} style={{
                color: '#639754'
              }} data-id="element-625" />
                  <span className="text-sm text-gray-600" data-id="element-626">
                    Positiv:{' '}
                    <span className="font-semibold" style={{
                  color: '#639754'
                }} data-id="element-627">
                      {currentTrend[currentTrend.length - 1].positivePercent}%
                    </span>
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-628">
                  <Minus size={16} style={{
                color: '#9a7d3a'
              }} data-id="element-629" />
                  <span className="text-sm text-gray-600" data-id="element-630">
                    Neutral:{' '}
                    <span className="font-semibold" style={{
                  color: '#9a7d3a'
                }} data-id="element-631">
                      {currentTrend[currentTrend.length - 1].neutralPercent}%
                    </span>
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-632">
                  <TrendingDown size={16} style={{
                color: '#E03C32'
              }} data-id="element-633" />
                  <span className="text-sm text-gray-600" data-id="element-634">
                    Negativ:{' '}
                    <span className="font-semibold" style={{
                  color: '#E03C32'
                }} data-id="element-635">
                      {currentTrend[currentTrend.length - 1].negativePercent}%
                    </span>
                  </span>
                </div>
              </div>
              <div className="text-sm text-gray-500" data-id="element-636">
                Veränderung Positiv:{' '}
                <span className="font-semibold" style={{
              color: '#639754'
            }} data-id="element-637">
                  +
                  {currentTrend[currentTrend.length - 1].positivePercent - currentTrend[0].positivePercent}{' '}
                  Pp.
                </span>
              </div>
            </div>}
        </div>
      </Card>

      {/* Topic Detail Dialog */}
      <AnimatePresence data-id="element-638">
        {isDialogOpen && selectedTopic && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 flex items-center justify-center z-[600] p-4" onClick={() => setIsDialogOpen(false)} data-id="element-639">
            <motion.div initial={{
          scale: 0.95,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.95,
          opacity: 0
        }} className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-640">
              <div className="p-6 border-b border-gray-200 bg-gray-50" data-id="element-641">
                <div className="flex items-center justify-between" data-id="element-642">
                  <div data-id="element-643">
                    <div className="flex items-center gap-3" data-id="element-644">
                      <h3 className="text-lg font-semibold text-gray-900" data-id="element-645">
                        {selectedTopic.name}
                      </h3>
                      <Badge variant={selectedTopic.sentiment === 'positive' ? 'success' : selectedTopic.sentiment === 'negative' ? 'danger' : 'warning'} data-id="element-646">
                        {selectedTopic.sentiment === 'positive' ? 'Positiv' : selectedTopic.sentiment === 'negative' ? 'Negativ' : 'Neutral'}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-500 mt-1" data-id="element-647">
                      {selectedTopic.size.toLocaleString('de-DE')} Erwähnungen
                      in Kundenbewertungen
                    </p>
                  </div>
                  <button onClick={() => setIsDialogOpen(false)} className="p-2 hover:bg-gray-200 rounded-full transition-colors" data-id="element-648">
                    <X size={20} data-id="element-649" />
                  </button>
                </div>
              </div>

              <div className="p-6 overflow-y-auto max-h-[60vh]" data-id="element-650">
                <div className="space-y-4" data-id="element-651">
                  {selectedTopic.comments.map((comment, index) => <motion.div key={index} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: index * 0.05
              }} className="p-4 bg-gray-50 rounded-lg border border-gray-100" data-id="element-652">
                      <div className="flex items-start justify-between gap-4 mb-2" data-id="element-653">
                        <div className="flex items-center gap-2" data-id="element-654">
                          <MapPin size={14} className="text-edeka-blue" data-id="element-655" />
                          <span className="text-sm font-medium text-gray-900" data-id="element-656">
                            {comment.market}
                          </span>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-657">
                          <div className="flex" data-id="element-658">
                            {[...Array(5)].map((_, i) => <span key={i} className={`text-sm ${i < comment.rating ? 'text-yellow-400' : 'text-gray-200'}`} data-id="element-659">
                                ★
                              </span>)}
                          </div>
                          <span className="text-xs text-gray-400 flex items-center gap-1" data-id="element-660">
                            <Calendar size={12} data-id="element-661" />
                            {new Date(comment.date).toLocaleDateString('de-DE')}
                          </span>
                        </div>
                      </div>
                      <p className="text-sm text-gray-700 italic" data-id="element-662">
                        "{comment.text}"
                      </p>
                    </motion.div>)}
                </div>
              </div>

              <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-end" data-id="element-663">
                <Button variant="primary" onClick={() => setIsDialogOpen(false)} data-id="element-664">
                  Schließen
                </Button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}